import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import type { FinanceCause, FinanceView } from "../api/types.js";
import { count, trx, utc } from "../format.js";
import { routeHref, type AdminScreen, type Router } from "../router.js";
import { Address, Button, ErrorNote, Note, Panel, ScreenHeader, Spinner, StatRow, Table, Td, Th, Tile } from "../ui.js";

/**
 * Finance (owner approved 2026-10-21): profit for a period, the balance now, where margin leaks, what one order earns
 * and how money moved. Read from GET /v1/admin/finance. Nothing is excluded: the owner's own wallets and orders count.
 * The other report pages stay as drill-downs: orders open their own page, venues the Providers screen.
 */

const PERIODS: Array<{ id: FinanceView["period"]; label: string }> = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "all", label: "All time" },
];

const METHOD_LABEL: Record<string, string> = {
  transfer: "Send TRX (transfer)",
  dashboard: "Balance (dashboard)",
  api: "API",
  x402: "x402",
  subscription: "Subscription",
  proxy: "Node proxy",
  promo: "Promo",
  bot: "Bot",
  batch: "Batch",
};

const CHAIN_LABEL: Record<string, string> = {
  sweeps: "Sweeps",
  venue_topups: "Venue top-ups",
  activations: "Activations",
  delegations: "Delegations and reclaims",
  other: "Other",
};

/** A link a middle-click opens: the router's own when it has one (the Nile tab), else the plain route. */
const hrefOf = (router: Router, screen: AdminScreen, param?: string) => router.hrefFor?.(screen, param) ?? routeHref(screen, param);

const tone = (sun: number) => (sun < 0 ? "danger" : "good");

/** Net profit per day as bars around zero. */
function DailyChart({ days }: { days: FinanceView["profit"]["daily"] }) {
  if (days.length === 0) return <p class="text-[11.5px] text-ink-muted">No money moved in this period.</p>;
  const w = 600;
  const h = 120;
  const max = Math.max(1, ...days.map((d) => Math.abs(d.net_sun)));
  const bar = w / days.length;
  const mid = h / 2;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} class="h-32 w-full" role="img" aria-label="Net profit per day">
      <line x1="0" x2={w} y1={mid} y2={mid} stroke="currentColor" class="text-line" />
      {days.map((d, i) => {
        const height = (Math.abs(d.net_sun) / max) * (mid - 4);
        return (
          <rect
            key={d.day}
            x={i * bar + bar * 0.15}
            width={bar * 0.7}
            y={d.net_sun >= 0 ? mid - height : mid}
            height={Math.max(1, height)}
            class={d.net_sun >= 0 ? "fill-save" : "fill-danger"}
          >
            <title>{`${d.day}: net ${trx(d.net_sun, 3)} · paid ${trx(d.paid_sun, 3)} · margin ${trx(d.margin_sun, 3)}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

function Cause({ title, note, cause, router }: { title: string; note: string; cause: FinanceCause; router: Router }) {
  return (
    <Panel title={title} subtitle={`${trx(cause.sun, 3)} over ${count(cause.orders)} orders. ${note}`}>
      <Table
        rows={cause.top.length}
        empty="Nothing here in this period."
        head={
          <tr>
            <Th>Order</Th>
            <Th>Time</Th>
            <Th numeric>Amount</Th>
          </tr>
        }
      >
        {cause.top.map((o) => (
          <tr key={o.order_id}>
            <Td mono>
              <a
                href={hrefOf(router, "orders", o.order_id)}
                onClick={(e) => router.onNavigate(e as MouseEvent, "orders", o.order_id)}
                class="underline"
              >
                {o.order_id.slice(0, 14)}…
              </a>
            </Td>
            <Td mono>{utc(o.at)}</Td>
            <Td numeric>{trx(o.sun, 3)}</Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

export function FinanceScreen({ router }: { router: Router }) {
  const [period, setPeriod] = useState<FinanceView["period"]>("week");
  const data = useResource((signal) => {
    void signal;
    return adminReads.finance(period);
  }, [period]);
  const view = data.data ?? null;

  return (
    <>
      <ScreenHeader
        title="Finance"
        lead="What we earned, what we hold, where margin leaks and how money moved. Venues count at their actual charge; the owner's wallets and orders are included."
        action={
          <div class="flex gap-1">
            {PERIODS.map((p) => (
              <Button key={p.id} tone={p.id === period ? "primary" : "ghost"} onClick={() => setPeriod(p.id)}>
                {p.label}
              </Button>
            ))}
          </div>
        }
      />
      {data.loading && view === null ? (
        <Spinner />
      ) : data.error !== null && view === null ? (
        <ErrorNote error={data.error} onRetry={() => data.reload()} />
      ) : view === null ? null : (
        <FinanceBody view={view} router={router} />
      )}
    </>
  );
}

function FinanceBody({ view, router }: { view: FinanceView; router: Router }) {
  const p = view.profit;
  const b = view.balance;
  const l = view.leakage;
  const f = view.flow;
  return (
    <div class="space-y-3">
      {/* 1. Profit */}
      <StatRow>
        <Tile
          label="Customers paid"
          value={trx(p.paid_sun, 2)}
          note={`Refunded ${trx(p.refunds_sun, 2)}.${p.in_progress.orders > 0 ? ` ${p.in_progress.orders} in progress (${trx(p.in_progress.paid_sun, 2)}) count once placed.` : ""}`}
        />
        <Tile label="Paid to venues" value={trx(p.venue_cost_sun, 2)} note={`At their actual charge. Own stake ${trx(p.own_stake_cost_sun, 2)}.`} />
        <Tile label="Margin" value={trx(p.margin_sun, 2)} tone={tone(p.margin_sun)} note="Paid less refunds, venues and own stake." />
        <Tile label="Net profit" value={trx(p.net_sun, 2)} tone={tone(p.net_sun)} note={`After ${trx(p.chain_cost_sun, 3)} of chain costs.`} />
      </StatRow>
      <div class="grid gap-3 xl:grid-cols-[2fr_1fr]">
        <Panel title="Net profit per day" subtitle="Hover a bar for the day's paid, margin and net. Days in UTC.">
          <DailyChart days={p.daily} />
        </Panel>
        <Panel title="Chain costs" subtitle="TRX burned on our own transactions; an activation also counts the TRX it sends.">
          <ul class="text-[12px]">
            {Object.entries(p.chain_cost_by_kind).map(([kind, sun]) => (
              <li key={kind} class="flex justify-between border-b border-line py-1 last:border-b-0">
                <span class="text-ink-muted">{CHAIN_LABEL[kind] ?? kind}</span>
                <span class="num">{trx(sun, 3)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {/* 2. Balance now */}
      <Panel
        title="Balance now"
        subtitle="Cash on our wallets plus credit at the venues, less what we owe customers on their balances."
      >
        {b.complete ? null : <Note tone="warning">A wallet could not be read just now: the totals are a lower bound.</Note>}
        <StatRow>
          <Tile label="On our wallets" value={b.wallets_sun === null ? null : trx(b.wallets_sun, 2)} note={b.wallets_usdt === null ? undefined : `and ${b.wallets_usdt.toFixed(2)} USDT`} />
          <Tile label="Credit at venues" value={trx(b.venue_credit_sun, 2)} note={`${count(b.venues.length)} venues with credit.`} />
          <Tile label="Owed to customers" value={b.customers_sun === null ? null : trx(b.customers_sun, 2)} note={b.customers_usdt === null ? undefined : `and ${b.customers_usdt.toFixed(2)} USDT`} />
          <Tile label="Own equity" value={b.equity_sun === null ? null : trx(b.equity_sun, 2)} tone={b.equity_sun === null ? "neutral" : tone(b.equity_sun)} note="TRX only; USDT is shown on each side." />
        </StatRow>
        <div class="grid gap-3 xl:grid-cols-2">
          <Table
            rows={b.wallets.length}
            empty="No wallet is configured."
            head={
              <tr>
                <Th>Wallet</Th>
                <Th>Address</Th>
                <Th numeric>TRX</Th>
                <Th numeric>USDT</Th>
              </tr>
            }
          >
            {b.wallets.map((w) => (
              <tr key={w.address}>
                <Td>{w.label}</Td>
                <Td>
                  <Address value={w.address} />
                </Td>
                <Td numeric>{trx(w.trx_sun, 2)}</Td>
                <Td numeric>{w.usdt === null ? "—" : w.usdt.toFixed(2)}</Td>
              </tr>
            ))}
          </Table>
          <Table
            rows={b.venues.length}
            empty="No venue holds our credit."
            head={
              <tr>
                <Th>Venue</Th>
                <Th numeric>Credit</Th>
                <Th>Read</Th>
              </tr>
            }
          >
            {b.venues.map((v) => (
              <tr key={v.provider}>
                <Td>
                  <a href={hrefOf(router, "providers")} onClick={(e) => router.onNavigate(e as MouseEvent, "providers")} class="underline">
                    {v.name ?? v.provider}
                  </a>
                </Td>
                <Td numeric>{trx(v.balance_sun, 2)}</Td>
                <Td mono>{utc(v.read_at)}</Td>
              </tr>
            ))}
          </Table>
        </div>
      </Panel>

      {/* 3. Leakage */}
      <StatRow>
        <Tile label="Expected fee" value={trx(l.expected_fee_sun, 2)} note="The routing fee the orders were priced with." />
        <Tile label="Real margin" value={trx(l.real_margin_sun, 2)} tone={tone(l.real_margin_sun)} />
        <Tile label="Leaked" value={trx(l.gap_sun, 2)} tone={l.gap_sun > 0 ? "warning" : "good"} note="Expected fee less real margin." />
      </StatRow>
      <div class="grid gap-3 xl:grid-cols-3">
        <Cause title="Venue overcharges" note="A venue billed more than the price we quoted from it (SoHu over-limit and others)." cause={l.venue_overcharge} router={router} />
        <Cause title="Negative-margin orders" note="Orders that cost us more than the customer kept paying." cause={l.negative_margin} router={router} />
        <Cause title="Refunds" note="Money given back: failed or partial fills." cause={l.refunds} router={router} />
      </div>

      {/* 4. Order economics */}
      <div class="grid gap-3 xl:grid-cols-2">
        <Panel title="Per order, by payment method" subtitle={`${count(view.economics.orders)} paid orders in this period. Averages per order.`}>
          <EconomicsTable rows={view.economics.by_method.map((r) => ({ key: r.method, label: METHOD_LABEL[r.method] ?? r.method, ...r }))} />
        </Panel>
        <Panel title="Per order, top clients" subtitle="The ten accounts that paid the most in this period.">
          <EconomicsTable
            rows={view.economics.top_clients.map((r) => ({
              key: r.account_id,
              label: r.owner === null ? r.account_id : `${r.owner.slice(0, 4)}…${r.owner.slice(-4)}`,
              href: hrefOf(router, "accounts", r.account_id),
              ...r,
            }))}
            router={router}
          />
        </Panel>
      </div>

      {/* 5. Money flow */}
      <Panel title="Money flow" subtitle="What came in from customers and what went out, in this period.">
        <StatRow>
          <Tile label="Deposits" value={trx(f.deposits_trx_sun, 2)} note={`and ${f.deposits_usdt.toFixed(2)} USDT credited to balances.`} />
          <Tile label="Send TRX payments" value={trx(f.transfers_in_sun, 2)} note={`${count(f.transfers_in)} transfers to our payment addresses.`} />
          <Tile label="Withdrawals" value={trx(f.withdrawals_sun, 2)} note={`${count(f.withdrawals)} sent to customers.`} />
          <Tile label="Swept to the collector" value={trx(f.sweeps_sun, 2)} note={`${count(f.sweeps)} sweeps.`} />
          <Tile label="Venue top-ups" value={trx(f.venue_topups_sun, 2)} note={`${count(f.venue_topups)} top-ups.`} />
        </StatRow>
      </Panel>
    </div>
  );
}

function EconomicsTable({
  rows,
  router,
}: {
  rows: Array<{ key: string; label: string; href?: string; orders: number; avg_revenue_sun: number; avg_margin_sun: number; avg_chain_cost_sun: number }>;
  router?: Router;
}) {
  return (
    <Table
      rows={rows.length}
      empty="No paid order in this period."
      head={
        <tr>
          <Th>Who</Th>
          <Th numeric>Orders</Th>
          <Th numeric>Revenue</Th>
          <Th numeric>Margin</Th>
          <Th numeric>Chain cost</Th>
        </tr>
      }
    >
      {rows.map((r) => (
        <tr key={r.key}>
          <Td>
            {r.href === undefined || router === undefined ? (
              r.label
            ) : (
              <a href={r.href} onClick={(e) => router.onNavigate(e as MouseEvent, "accounts", r.key)} class="underline">
                {r.label}
              </a>
            )}
          </Td>
          <Td numeric>{count(r.orders)}</Td>
          <Td numeric>{trx(r.avg_revenue_sun, 3)}</Td>
          <Td numeric class={r.avg_margin_sun < 0 ? "text-danger" : ""}>
            {trx(r.avg_margin_sun, 3)}
          </Td>
          <Td numeric>{trx(r.avg_chain_cost_sun, 3)}</Td>
        </tr>
      ))}
    </Table>
  );
}
